import {
  List,
  Datagrid,
  ReferenceField,
  ChipField,
  BooleanField,
  ArrayField,
  NumberField,
  ImageField,
  ReferenceInput,
  SelectInput,
  BooleanInput,
  DeleteWithConfirmButton,
  Edit,
  TextInput,
  ImageInput,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  Create,
  Show,
  TextField,
  FormDataConsumer,
  useRecordContext,
} from 'react-admin'
import { languages, questionTypes } from '../config/constants'
import { LangRelatedTabbedForm, LangRelatedTabbedShow } from '../components'
import { Paper } from '@mui/material'

const transformQuestionData = (data) => {
  // Clean up options array - remove null/empty values from option objects
  // and remove flat option.* fields that React Admin creates
  if (data.options && Array.isArray(data.options)) {
    console.log('transformQuestionData invoked')
    console.log('data.options', data.options)
    data.options = data.options
      .map((option) => {
        if (option) {
          // Remove flat option.* fields (option.eng, option.ukr, etc.)
          const cleanedOption = { ...option }
          languages.forEach((lang) => {
            delete cleanedOption[`option.${lang.id}`]
          })

          // Clean up the nested option object - remove null/empty values
          if (cleanedOption.option) {
            const cleanedNestedOption = {}
            languages.forEach((lang) => {
              const value = cleanedOption.option[lang.id]
              if (value != null && value !== '') {
                cleanedNestedOption[lang.id] = value
              }
            })
            cleanedOption.option = cleanedNestedOption
          }

          return cleanedOption
        }
        return option
      })
      .filter(Boolean) // Remove any null/undefined options
  }
  return data
}

// <Edit title={<PostTitle />}>
// const PostTitle = () => {
//   const record = useRecordContext()
//   return <span>Post {record ? `'${record.title}'` : ''}</span>
// }

export const QuestionList = ({ isEdit, isDelete }) => (
  <List
    filters={[
      <ReferenceInput key="filter-sectionId" source="sectionId" reference="sections" />,
      <SelectInput key="filter-type" source="type" choices={questionTypes} />,
      <BooleanInput key="filter-isCorrectRequired" source="isCorrectRequired" />,
    ]}
  >
    <Datagrid bulkActionButtons={false} rowClick={isEdit ? 'edit' : 'show'}>
      <ReferenceField source="sectionId" reference="sections" />
      <TextField source="question.eng" />
      <ChipField source="type" />
      <BooleanField source="isCorrectRequired" />
      <NumberField source="points" />
      {isDelete && (
        <DeleteWithConfirmButton confirmContent="This action could not be reverted. Are you sure?" />
      )}
    </Datagrid>
  </List>
)

export const QuestionEdit = () => (
  <Edit>
    <LangRelatedTabbedForm
      transform={transformQuestionData}
      generalFields={[
        <ImageInput key="image" source="image" accept="image/*">
          <ImageField source="src" title="title" />
        </ImageInput>,
        <ImageField key="imageUrl" source="imageUrl" title="imageUrl" />,
        <ReferenceInput key="sectionId" source="sectionId" reference="sections" />,
        <SelectInput key="type" source="type" choices={questionTypes} />,
        <FormDataConsumer key="options">
          {({ formData }) => {
            const type = formData?.type
            const showOptions =
              type === 'radioButton' || type === 'dropdown' || type === 'checkbox'
            console.log('formData', formData)
            return showOptions ? (
              <Paper sx={{ p: 2, mt: 2, mb: 2 }}>
                <ArrayInput source="options">
                  <SimpleFormIterator
                    getItemLabel={(index) => `#${index + 1}`}
                    sx={{
                      // Keep the item label from growing and colliding with iterator action buttons
                      '& .RaSimpleFormIterator-index': {
                        display: 'block !important',
                        width: '4ch',
                        minWidth: '4ch',
                        maxWidth: '4ch',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pt: 0.5,
                      },
                      '@media (max-width: 899.95px)': {
                        '& .RaSimpleFormIterator-index': {
                          display: 'block !important',
                        },
                      },
                      // Inputs area becomes 1col on mobile, 2col on big screens
                      '& .RaSimpleFormIterator-form': {
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: '1fr 1fr 1fr 0.6fr',
                        },
                        gap: 1,
                        alignItems: 'flex-start',
                        width: '100%',
                        pt: 1.5,
                        // flex: '1 1 0',
                        minWidth: 0,
                      },
                      '& .RaSimpleFormIterator-action': {
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    {languages.map((lang) => (
                      <TextInput
                        key={lang.id}
                        source={`option.${lang.id}`}
                        label={`Option (${lang.label})`}
                        fullWidth
                      />
                    ))}
                    <BooleanInput source="isCorrect" />
                  </SimpleFormIterator>
                </ArrayInput>
              </Paper>
            ) : null
          }}
        </FormDataConsumer>,
        <BooleanInput key="isCorrectRequired" source="isCorrectRequired" />,
        <NumberInput key="points" source="points" />,
      ]}
      tabFields={(lang) => [
        <TextInput
          key={`question-${lang.id}`}
          source={`question.${lang.id}`}
          fullWidth
          multiline
          rows={4}
        />,
        <FormDataConsumer key={`answer-${lang.id}`}>
          {({ formData }) => {
            const type = formData?.type
            const showAnswer =
              type === 'stringMatch' ||
              type === 'freeString' ||
              type === 'number' ||
              type === 'boolean'
            return showAnswer ? (
              <TextInput source={`answer.${lang.id}`} fullWidth multiline rows={4} />
            ) : null
          }}
        </FormDataConsumer>,
      ]}
    />
  </Edit>
)

export const QuestionCreate = () => (
  <Create>
    <LangRelatedTabbedForm
      transform={transformQuestionData}
      generalFields={[
        <ImageInput key="image" source="image" accept="image/*">
          <ImageField source="src" title="title" />
        </ImageInput>,
        <ReferenceInput key="sectionId" source="sectionId" reference="sections" />,
        <SelectInput key="type" source="type" choices={questionTypes} />,
        <FormDataConsumer key="options">
          {({ formData }) => {
            const type = formData?.type
            const showOptions =
              type === 'radioButton' || type === 'dropdown' || type === 'checkbox'
            return showOptions ? (
              <Paper sx={{ p: 2, mt: 2, mb: 2 }}>
                <ArrayInput source="options">
                  <SimpleFormIterator
                    getItemLabel={(index) => `#${index + 1}`}
                    sx={{
                      // Keep the item label from growing and colliding with iterator action buttons
                      '& .RaSimpleFormIterator-index': {
                        display: 'block !important',
                        width: '4ch',
                        minWidth: '4ch',
                        maxWidth: '4ch',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pt: 0.5,
                      },
                      '@media (max-width: 899.95px)': {
                        '& .RaSimpleFormIterator-index': {
                          display: 'block !important',
                        },
                      },
                      // Inputs area becomes 1col on mobile, 2col on big screens
                      '& .RaSimpleFormIterator-form': {
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: '1fr 1fr 1fr 0.6fr',
                        },
                        gap: 1,
                        alignItems: 'flex-start',
                        width: '100%',
                        pt: 1.5,
                        // flex: '1 1 0',
                        minWidth: 0,
                      },
                      '& .RaSimpleFormIterator-action': {
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    {languages.map((lang) => (
                      <TextInput
                        key={lang.id}
                        source={`option.${lang.id}`}
                        label={`Option (${lang.label})`}
                        fullWidth
                      />
                    ))}
                    <BooleanInput source="isCorrect" />
                  </SimpleFormIterator>
                </ArrayInput>
              </Paper>
            ) : null
          }}
        </FormDataConsumer>,
        <BooleanInput key="isCorrectRequired" source="isCorrectRequired" />,
        <NumberInput key="points" source="points" />,
      ]}
      tabFields={(lang) => [
        <TextInput
          key={`question-${lang.id}`}
          source={`question.${lang.id}`}
          fullWidth
          multiline
          rows={4}
        />,
        <FormDataConsumer key={`answer-${lang.id}`}>
          {({ formData }) => {
            const type = formData?.type
            const showAnswer =
              type === 'stringMatch' ||
              type === 'freeString' ||
              type === 'number' ||
              type === 'boolean'
            return showAnswer ? (
              <TextInput source={`answer.${lang.id}`} fullWidth multiline rows={4} />
            ) : null
          }}
        </FormDataConsumer>,
      ]}
    />
  </Create>
)

const ConditionalOptionsField = () => {
  const record = useRecordContext()
  const type = record?.type
  const showOptions = type === 'radioButton' || type === 'dropdown' || type === 'checkbox'

  return showOptions ? (
    <Paper sx={{ p: 2, mt: 2, mb: 2 }}>
      <ArrayField source="options">
        <Datagrid>
          {languages.map((lang) => (
            <TextField
              key={lang.id}
              source={`option.${lang.id}`}
              label={`Option (${lang.label})`}
            />
          ))}
          <BooleanField source="isCorrect" />
        </Datagrid>
      </ArrayField>
    </Paper>
  ) : null
}

const ConditionalAnswerField = ({ lang }) => {
  const record = useRecordContext()
  const type = record?.type
  const showAnswer =
    type === 'stringMatch' || type === 'freeString' || type === 'number' || type === 'boolean'

  return showAnswer ? <TextField source={`answer.${lang.id}`} /> : null
}

export const QuestionShow = () => (
  <Show>
    <LangRelatedTabbedShow
      generalFields={[
        <ImageField key="imageUrl" source="imageUrl" title="imageUrl" />,
        <ReferenceField key="sectionId" source="sectionId" reference="sections" />,
        <ChipField key="type" source="type" />,
        <ConditionalOptionsField key="options" />,
        <BooleanField key="isCorrectRequired" source="isCorrectRequired" />,
        <NumberField key="points" source="points" />,
      ]}
      tabFields={(lang) => [
        <TextField key={`question-${lang.id}`} source={`question.${lang.id}`} />,
        <ConditionalAnswerField key={`answer-${lang.id}`} lang={lang} />,
      ]}
    />
  </Show>
)
